import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

// The languages that really work. English is the default and needs no rows.
export const LANGS = [
  ["en", "English"],
  ["hi", "हिन्दी · Hindi"],
  ["mr", "मराठी · Marathi"],
  ["bn", "বাংলা · Bengali"],
  ["te", "తెలుగు · Telugu"],
  ["ta", "தமிழ் · Tamil"],
  ["gu", "ગુજરાતી · Gujarati"],
  ["kn", "ಕನ್ನಡ · Kannada"],
  ["es", "Español · Spanish"],
  ["fr", "Français · French"],
  ["pt", "Português · Portuguese"],
  ["de", "Deutsch · German"],
  ["id", "Bahasa Indonesia · Indonesian"],
];
const CODES = LANGS.map((l) => l[0]);
const I18nContext = createContext({ lang: "en", setLang: () => {}, t: (s) => s, tf: (s) => s });

function readLocal() {
  try {
    const v = localStorage.getItem("jobky.lang");
    return CODES.includes(v) ? v : "en";
  } catch (e) {
    return "en";
  }
}

function tellBot(code) {
  try {
    const f = document.querySelector('iframe[data-testid="bot-iframe"]');
    if (f && f.contentWindow) f.contentWindow.postMessage({ type: "jobky-lang", lang: code }, "*");
  } catch (e) {}
}

export function I18nProvider({ children }) {
  const { user } = useAuth();
  const [lang, setLangState] = useState(readLocal());
  const [strings, setStrings] = useState({});
  const adopted = useRef(false);

  // Adopt the account's language on a device with no local choice yet,
  // so the choice follows the user to other devices.
  useEffect(() => {
    if (!user || adopted.current) return;
    adopted.current = true;
    let local = null;
    try { local = localStorage.getItem("jobky.lang"); } catch (e) {}
    if (!local && user.language && CODES.includes(user.language) && user.language !== "en") {
      try { localStorage.setItem("jobky.lang", user.language); } catch (e) {}
      setLangState(user.language);
    }
  }, [user]);

  // Load the translations and set <html lang> whenever the language changes.
  useEffect(() => {
    document.documentElement.lang = lang;
    if (lang === "en") {
      setStrings({});
      return;
    }
    let live = true;
    api
      .get(`/i18n/${lang}`)
      .then((r) => { if (live) setStrings(r.data.strings || {}); })
      .catch(() => {});
    return () => { live = false; };
  }, [lang]);

  // Follow changes made in another tab or inside the bot page (shared localStorage).
  useEffect(() => {
    const h = (e) => {
      if (e.key === "jobky.lang" && e.newValue && CODES.includes(e.newValue) && e.newValue !== lang) {
        setLangState(e.newValue);
      }
    };
    window.addEventListener("storage", h);
    return () => window.removeEventListener("storage", h);
  }, [lang]);

  const setLang = useCallback(
    (code) => {
      if (!CODES.includes(code)) code = "en";
      try { localStorage.setItem("jobky.lang", code); } catch (e) {}
      setLangState(code);
      // Tell the bot page live (it also reads jobky.lang on boot and listens to the storage event).
      tellBot(code);
      // Save on the account so the choice follows the user to other devices.
      if (user) api.put("/me/language", { lang: code }).catch(() => {});
    },
    [user]
  );

  const t = useCallback((s) => strings[s] || s, [strings]);
  const tf = useCallback(
    (s, vars) =>
      (strings[s] || s).replace(/\{(\w+)\}/g, (m, k) =>
        vars && vars[k] != null ? String(vars[k]) : m
      ),
    [strings]
  );

  return <I18nContext.Provider value={{ lang, setLang, t, tf, langs: LANGS }}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}
